'use client';

import { useCallback, useSyncExternalStore } from 'react';

/**
 * Tracks a CSS media query in JS, for when a component must not *mount* below a
 * breakpoint rather than merely be hidden there.
 *
 * Tailwind's `hidden lg:flex` only sets `display: none` — React still mounts the
 * subtree and its effects still run. For a decorative WebGL canvas that means
 * phones pay full renderer setup, GPU memory and rAF cost for a panel nobody can
 * see. Gating on this hook skips the mount entirely.
 *
 * `matchMedia` is an external store, so this subscribes to it rather than
 * mirroring it into state via an effect. The server snapshot is always `false`,
 * so server and hydration markup agree and React re-renders once with the real
 * value — no hydration mismatch, and no cascading-render lint escape hatch.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return () => {};
      }

      const mediaQuery = window.matchMedia(query);
      mediaQuery.addEventListener('change', onStoreChange);
      return () => mediaQuery.removeEventListener('change', onStoreChange);
    },
    [query],
  );

  const getSnapshot = useCallback(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(query).matches;
  }, [query]);

  // Never assume the breakpoint matches on the server: the background is
  // opt-in, so guessing wrong here would mount WebGL on a phone again.
  const getServerSnapshot = useCallback(() => false, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
